/**
 * The address decides the language (docs/seo-plan.md): Hebrew at the plain
 * addresses (/shuls), English under /en (/en/shuls). Google and first-time
 * visitors see the language of the address; a language the visitor picked
 * before (saved in the browser) moves them to their copy.
 */

export type SiteLang = 'he' | 'en';

export const EN_PREFIX = '/en';
/** Saved when the visitor presses the language button. */
export const LANG_STORAGE_KEY = 'site-lang';
/** Older key (i18next's detector). Only an exact 'he'/'en' counts as a choice. */
export const OLD_LANG_STORAGE_KEY = 'shul-times-lang';

export function langFromPath(pathname: string): SiteLang {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`) ? 'en' : 'he';
}

/** "/en/shuls" → "/shuls", "/en" → "/", "/shuls" → "/shuls". */
export function stripLangPrefix(pathname: string): string {
  if (langFromPath(pathname) === 'he') return pathname || '/';
  return pathname.slice(EN_PREFIX.length) || '/';
}

/** A path without the language part ("/shuls") as an address in `lang`. */
export function pathInLang(path: string, lang: SiteLang): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (lang === 'he') return clean;
  return clean === '/' ? EN_PREFIX : `${EN_PREFIX}${clean}`;
}

/** The same address in the other language copy (path only). */
export function switchLangPath(pathname: string, lang: SiteLang): string {
  return pathInLang(stripLangPrefix(pathname), lang);
}

/** The visitor's own earlier choice, or null (first visit, or Google). */
export function readSavedLang(storage: Pick<Storage, 'getItem'> | null | undefined): SiteLang | null {
  if (!storage) return null;
  try {
    for (const key of [LANG_STORAGE_KEY, OLD_LANG_STORAGE_KEY]) {
      const value = storage.getItem(key);
      if (value === 'he' || value === 'en') return value;
    }
  } catch {
    // Storage blocked (private mode): no saved choice.
  }
  return null;
}

export function saveLang(storage: Pick<Storage, 'setItem'> | null | undefined, lang: SiteLang): void {
  try {
    storage?.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Storage blocked: the choice just isn't remembered.
  }
}

/**
 * Which language to start in, and the address to move to when a saved choice
 * differs from the address (null = stay).
 */
export function startLang(pathname: string, saved: SiteLang | null): { lang: SiteLang; moveTo: string | null } {
  const fromPath = langFromPath(pathname);
  if (saved && saved !== fromPath) return { lang: saved, moveTo: switchLangPath(pathname, saved) };
  return { lang: fromPath, moveTo: null };
}
